import React, { useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

export const GoogleCalendarReauthBanner: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [checked, setChecked] = useState(false);

  // Proactively check if Google Calendar credentials are still valid
  useEffect(() => {
    if (!user?.google_calendar_enabled || checked) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await api.get('/events/google-calendar/check');
        if (!cancelled && !response.data.valid) {
          // Backend auto-disabled the calendar — refresh user to get updated state
          await refreshUser();
        }
      } catch {
        // Network error or 401 — don't block the UI
      } finally {
        if (!cancelled) setChecked(true);
      }
    })();

    return () => { cancelled = true; };
  }, [user?.google_calendar_enabled, checked, refreshUser]);

  // Show banner when calendar was previously connected but is now disabled
  const needsReauth = user
    && !user.google_calendar_enabled
    && user.google_calendar_last_sync;

  if (!needsReauth || dismissed) return null;

  const handleReconnect = () => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    const redirectUri = `${window.location.origin}/settings`;
    const scope = 'https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/calendar.events';

    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${clientId}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `response_type=code&` +
      `scope=${encodeURIComponent(scope)}&` +
      `access_type=offline&` +
      `prompt=consent`;

    window.location.href = authUrl;
  };

  return (
    <div className="bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-700">
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <span className="text-sm text-amber-800 dark:text-amber-200 truncate">
            Google Calendar disconnected — authorization expired.
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleReconnect}
            className="px-3 py-1 text-sm font-medium bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors"
          >
            Reconnect
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="p-1 text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200 transition-colors"
            title="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
